import { Commit, Dispatch } from 'vuex'
import { isApiError } from '@/types/api'
import {
  createWorkflowConfig as apiCreateWorkflowConfig,
  deleteWorkflowConfig as apiDeleteWorkflowConfig,
  getHydratedWorkflowRun as apiGetHydratedWorkflowRun,
  listWorkflowConfigs as apiListWorkflowConfigs,
  listWorkflowRunsForWorkflow as apiListWorkflowRunsForWorkflow,
  updateWorkflowConfig as apiUpdateWorkflowConfig
} from '@/utils/api'
import type { WorkflowConfig, WorkflowRunHydratedRecord, WorkflowRunRecord } from '@/types/workflows'

export interface WorkflowsState {
  configs: Record<string, WorkflowConfig>
  runIdsByWorkflowId: Record<string, string[]>
  runsById: Record<string, WorkflowRunRecord>
  hydratedRunsById: Record<string, WorkflowRunHydratedRecord>
  loadingRunLists: Record<string, boolean>
  loadingHydratedRuns: Record<string, boolean>
  saveTokensByWorkflowId: Record<string, number>
}

export const workflows = () => ({
  state: <WorkflowsState>{
    configs: {},
    runIdsByWorkflowId: {},
    runsById: {},
    hydratedRunsById: {},
    loadingRunLists: {},
    loadingHydratedRuns: {},
    saveTokensByWorkflowId: {}
  },
  mutations: {
    setWorkflowConfigs(state: WorkflowsState, configs: WorkflowConfig[]) {
      state.configs = configs.reduce((acc, config) => {
        acc[config.id] = config
        return acc
      }, {} as Record<string, WorkflowConfig>)
    },
    setWorkflowConfig(state: WorkflowsState, config: WorkflowConfig) {
      state.configs[config.id] = config
    },
    deleteWorkflowConfig(state: WorkflowsState, workflowId: string) {
      delete state.configs[workflowId]
      delete state.runIdsByWorkflowId[workflowId]
    },
    setWorkflowRuns(
      state: WorkflowsState,
      {
        workflowId,
        runs,
        append
      }: {
        workflowId: string
        runs: WorkflowRunRecord[]
        append?: boolean
      }
    ) {
      runs.forEach((run) => {
        state.runsById[run.runId] = run
      })

      const existingIds = append ? state.runIdsByWorkflowId[workflowId] || [] : []
      const mergedIds = [...existingIds, ...runs.map((run) => run.runId)]
      state.runIdsByWorkflowId[workflowId] = [...new Set(mergedIds)].sort((a, b) => {
        const aRun = state.runsById[a]
        const bRun = state.runsById[b]
        return (bRun?.updatedAt || 0) - (aRun?.updatedAt || 0)
      })
    },
    mergeWorkflowRun(state: WorkflowsState, run: WorkflowRunRecord) {
      state.runsById[run.runId] = run
      if (!run.workflowConfigId) return
      const ids = new Set(state.runIdsByWorkflowId[run.workflowConfigId] || [])
      ids.add(run.runId)
      state.runIdsByWorkflowId[run.workflowConfigId] = [...ids].sort((a, b) => {
        const aRun = state.runsById[a]
        const bRun = state.runsById[b]
        return (bRun?.updatedAt || 0) - (aRun?.updatedAt || 0)
      })
    },
    setHydratedWorkflowRun(state: WorkflowsState, hydrated: WorkflowRunHydratedRecord) {
      state.hydratedRunsById[hydrated.record.runId] = hydrated
      state.runsById[hydrated.record.runId] = hydrated.record
      if (hydrated.record.workflowConfigId) {
        const ids = new Set(state.runIdsByWorkflowId[hydrated.record.workflowConfigId] || [])
        ids.add(hydrated.record.runId)
        state.runIdsByWorkflowId[hydrated.record.workflowConfigId] = [...ids].sort((a, b) => {
          const aRun = state.runsById[a]
          const bRun = state.runsById[b]
          return (bRun?.updatedAt || 0) - (aRun?.updatedAt || 0)
        })
      }
    },
    setWorkflowRunListLoading(state: WorkflowsState, { workflowId, value }: { workflowId: string; value: boolean }) {
      state.loadingRunLists[workflowId] = value
    },
    setHydratedWorkflowRunLoading(state: WorkflowsState, { runId, value }: { runId: string; value: boolean }) {
      state.loadingHydratedRuns[runId] = value
    },
    setWorkflowSaveToken(state: WorkflowsState, { workflowId, token }: { workflowId: string; token: number }) {
      state.saveTokensByWorkflowId[workflowId] = token
    },
    clearWorkflowRunCacheForWorkflow(state: WorkflowsState, workflowId: string) {
      const runIds = state.runIdsByWorkflowId[workflowId] || []
      runIds.forEach((runId) => {
        delete state.runsById[runId]
        delete state.hydratedRunsById[runId]
        delete state.loadingHydratedRuns[runId]
      })
      delete state.runIdsByWorkflowId[workflowId]
      delete state.loadingRunLists[workflowId]
    }
  },
  actions: {
    async fetchWorkflows({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await apiListWorkflowConfigs()
      if (isApiError(response)) {
        dispatch('addLogEvent', { title: 'Workflow Fetch Error', content: response }, { root: true })
        return false
      }
      commit('setWorkflowConfigs', response.data)
      return true
    },
    async fetchWorkflowRuns(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      {
        workflowId,
        limit = 20,
        offset = 0,
        append = false
      }: {
        workflowId: string
        limit?: number
        offset?: number
        append?: boolean
      }
    ) {
      commit('setWorkflowRunListLoading', { workflowId, value: true })
      try {
        const response = await apiListWorkflowRunsForWorkflow(workflowId, limit, offset)
        if (isApiError(response) || !response.success) {
          dispatch(
            'addLogEvent',
            { title: 'Workflow Run Fetch Error', content: isApiError(response) ? response : 'Unable to fetch workflow runs' },
            { root: true }
          )
          return false
        }
        commit('setWorkflowRuns', { workflowId, runs: response.data, append })
        return true
      } finally {
        commit('setWorkflowRunListLoading', { workflowId, value: false })
      }
    },
    async fetchHydratedWorkflowRun(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: WorkflowsState },
      runId: string
    ) {
      if (state.loadingHydratedRuns[runId]) return true
      commit('setHydratedWorkflowRunLoading', { runId, value: true })
      try {
        const response = await apiGetHydratedWorkflowRun(runId)
        if (isApiError(response) || !response.success) {
          dispatch(
            'addLogEvent',
            { title: 'Workflow Hydrated Run Fetch Error', content: isApiError(response) ? response : 'Unable to fetch hydrated workflow run' },
            { root: true }
          )
          return false
        }
        commit('setHydratedWorkflowRun', response.data)
        return true
      } finally {
        commit('setHydratedWorkflowRunLoading', { runId, value: false })
      }
    },
    async saveWorkflow(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: WorkflowsState },
      config: WorkflowConfig
    ) {
      const nextToken = (state.saveTokensByWorkflowId[config.id] || 0) + 1
      commit('setWorkflowSaveToken', { workflowId: config.id, token: nextToken })
      const exists = Boolean(state.configs[config.id])
      const response = exists
        ? await apiUpdateWorkflowConfig(config.id, config)
        : await apiCreateWorkflowConfig(config)

      if (isApiError(response) || !response.success) {
        dispatch(
          'updateSnackbar',
          { text: `Workflow Save Error: ${isApiError(response) ? response.message : 'Unable to save workflow'}`, color: 'error' },
          { root: true }
        )
        dispatch('addLogEvent', { title: 'Workflow Save Error', content: response }, { root: true })
        return false
      }

      if (state.saveTokensByWorkflowId[config.id] !== nextToken) {
        return true
      }

      commit('setWorkflowConfig', response.data)
      dispatch('markConfigResourcesDirty', ['workflows'], { root: true } as any)
      return true
    },
    async deleteWorkflow({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, workflowId: string) {
      const response = await apiDeleteWorkflowConfig(workflowId)
      if (isApiError(response)) {
        dispatch(
          'updateSnackbar',
          { text: `Workflow Delete Error: ${response.message}`, color: 'error' },
          { root: true }
        )
        dispatch('addLogEvent', { title: 'Workflow Delete Error', content: response }, { root: true })
        return false
      }
      commit('deleteWorkflowConfig', workflowId)
      dispatch('markConfigResourcesDirty', ['workflows'], { root: true } as any)
      return true
    }
  },
  getters: {
    workflowList: (state: WorkflowsState): WorkflowConfig[] => {
      return Object.values(state.configs).sort((a, b) => (b.updatedAt ?? b.timestamp ?? 0) - (a.updatedAt ?? a.timestamp ?? 0))
    },
    workflowRunsByWorkflow:
      (state: WorkflowsState) =>
      (workflowId: string): WorkflowRunRecord[] => {
        return (state.runIdsByWorkflowId[workflowId] || [])
          .map((runId) => state.runsById[runId])
          .filter(Boolean)
      },
    getHydratedWorkflowRun:
      (state: WorkflowsState) =>
      (runId: string): WorkflowRunHydratedRecord | undefined => {
        return state.hydratedRunsById[runId]
      },
    isWorkflowRunListLoading:
      (state: WorkflowsState) =>
      (workflowId: string): boolean => {
        return Boolean(state.loadingRunLists[workflowId])
      },
    isHydratedWorkflowRunLoading:
      (state: WorkflowsState) =>
      (runId: string): boolean => {
        return Boolean(state.loadingHydratedRuns[runId])
      },
    getWorkflowById:
      (state: WorkflowsState) =>
      (id: string): WorkflowConfig | undefined => {
        return state.configs[id]
      }
  }
})
